import React, { useState } from 'react';
import { ArrowLeft, Sprout, Sun, Calculator, ChevronRight, Search, Leaf, Droplets } from 'lucide-react';
import ETOCalculator from './ETOCalculator';
import KcDatabase from './KcDatabase';

interface OpenRiegoProps {
  onBack: () => void;
}

interface RiegoTool {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  color: string;
  gradient: string;
}

const OpenRiego: React.FC<OpenRiegoProps> = ({ onBack }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('Todos');
  const [activeTool, setActiveTool] = useState<string | null>(null);

  const riegoTools: RiegoTool[] = [
  {
    id: 'eto',
    title: 'Evapotranspiración de Referencia (ET0)',
    description: 'Calcula la ET0 usando el método de Penman-Monteith FAO-56 con datos climáticos precisos para una gestión eficiente del riego.',
    icon: <Sun className="w-7 h-7" />,
    category: 'Evapotranspiración',
    color: 'text-amber-600',
    gradient: 'from-amber-500 to-orange-500'
  },
  {
    id: 'kc',
    title: 'Coeficientes de Cultivo FAO(Kc)-1998',
    description: 'Accede a la base de datos completa de coeficientes Kc para diferentes cultivos y etapas fenológicas basados en FAO-56.',
    icon: <Leaf className="w-7 h-7" />,
    category: 'Coeficientes',
    color: 'text-emerald-600',
    gradient: 'from-emerald-500 to-green-500'
  }];


  const categories = ['Todos', ...Array.from(new Set(riegoTools.map((t) => t.category)))];

  const filtered = riegoTools.filter((tool) => {
    const matchSearch =
    tool.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tool.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCat = activeFilter === 'Todos' || tool.category === activeFilter;
    return matchSearch && matchCat;
  });

  // Render dedicated tools full-screen
  if (activeTool === 'eto') {
    return <ETOCalculator onBack={() => setActiveTool(null)} />;
  }
  if (activeTool === 'kc') {
    return <KcDatabase onBack={() => setActiveTool(null)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-emerald-900 via-green-800 to-teal-900">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-lime-400/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
          <svg className="absolute bottom-0 left-0 w-full opacity-10" viewBox="0 0 1440 120" fill="none">
            <path d="M0,64 C360,120 720,0 1080,64 C1260,96 1380,80 1440,64 L1440,120 L0,120 Z" fill="white" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-emerald-200 hover:text-white mb-8 transition-colors group">

            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Volver a Herramientas HH</span>
          </button>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                  <Sprout className="w-7 h-7 text-emerald-300" />
                </div>
                <span className="text-emerald-300 font-semibold text-lg tracking-wide uppercase">openRiego</span>
              </div>
              


              <p className="text-lg text-emerald-100 leading-relaxed">
                Plataforma especializada en el <strong className="text-emerald-300">diseño y gestión del riego</strong>: estima necesidades hídricas, evapotranspiración de referencia y coeficientes de cultivo para una agricultura eficiente.
              </p>
            </div>

            {/* Highlight card */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 min-w-[280px]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center">
                  <Droplets className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">Gestión hídrica</p>
                  <p className="text-emerald-200 text-xs">Riego de precisión</p>
                </div>
              </div>
              <div className="text-emerald-100 text-sm leading-relaxed">
                Optimiza el uso del agua en tus cultivos con cálculos basados en estándares <strong className="text-white">FAO-56</strong>.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4 flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar herramienta..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all" />

          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 w-full md:w-auto">
            {categories.map((cat) =>
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeFilter === cat ?
              'bg-emerald-600 text-white shadow-md' :
              'bg-gray-100 text-gray-600 hover:bg-gray-200'}`
              }>

                {cat}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Subtitle */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Seleccione la herramienta que desea utilizar</h2>
        <p className="text-gray-500 mt-1">{filtered.length} herramienta{filtered.length !== 1 ? 's' : ''} disponible{filtered.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Tools Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((tool, index) =>
          <div
            key={tool.id}
            onClick={() => setActiveTool(tool.id)}
            className="group relative bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
            style={{ animationDelay: `${index * 60}ms` }}>

              <div className={`h-1.5 bg-gradient-to-r ${tool.gradient}`}></div>

              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tool.gradient} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {tool.icon}
                  </div>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full bg-gray-100 ${tool.color}`}>
                    {tool.category}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-emerald-700 transition-colors leading-snug">
                  {tool.title}
                </h3>

                <p className="text-sm text-gray-500 leading-relaxed mb-5">
                  {tool.description}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-gray-400" />
                    <span className="text-xs text-gray-400 font-medium">Herramienta</span>
                  </div>
                  <div className="flex items-center gap-1 text-emerald-600 font-semibold text-sm group-hover:gap-3 transition-all">
                    <span>Abrir</span>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </div>

              <div className={`absolute inset-0 opacity-0 group-hover:opacity-5 bg-gradient-to-br ${tool.gradient} transition-opacity duration-300 pointer-events-none rounded-2xl`}></div>
            </div>
          )}
        </div>

        {filtered.length === 0 &&
        <div className="text-center py-16">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-500 font-medium">No se encontraron herramientas</p>
            <p className="text-gray-400 mt-1">Intenta con otro término de búsqueda</p>
          </div>
        }
      </div>

      {/* Footer */}
      <div className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
          <p className="text-gray-400 text-sm">© 2025 OpenHH — openRiegoWeb | Herramientas para gestión del riego</p>
        </div>
      </div>
    </div>);

};

export default OpenRiego;